import { useCallback, useMemo, useState } from "react";

import type { OverSiteControllerPeopleNumInput } from "@/lib/gql/__generated__/graphql";
import { DayOfTheWeek } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { useProductRoomFormModelContext } from "../../../../context";
import { createEmptyPriceItem } from "../_constants/empty-price-item";

export const OVER_SITE_CONTROLLER_PEOPLE_NUM_TYPE = ["NotUse", "StaticPrice", "DynamicPrice"] as const;
export type OverSiteControllerPeopleNumType = (typeof OVER_SITE_CONTROLLER_PEOPLE_NUM_TYPE)[number];

const WEEKEND_PRICE_DEFAULT_VALUE = {
	availableWeekDays: {
		isEdited: true,
		hasError: false,
		value: [DayOfTheWeek.Sat, DayOfTheWeek.Sun].map((item) => {
			return {
				isEdited: true,
				value: item,
			};
		}),
	},
	prices: {
		isEdited: true,
		hasError: false,
		value: [],
	},
} satisfies ForAutoGenerator<OverSiteControllerPeopleNumInput, "">;

const WEEKDAY_PRICE_DEFAULT_VALUE = {
	availableWeekDays: {
		isEdited: true,
		hasError: false,
		value: [DayOfTheWeek.Mon, DayOfTheWeek.Tue, DayOfTheWeek.Wed, DayOfTheWeek.Thu, DayOfTheWeek.Fri].map(
			(item) => {
				return {
					isEdited: true,
					value: item,
				};
			},
		),
	},
	prices: {
		isEdited: true,
		hasError: false,
		value: [],
	},
} satisfies ForAutoGenerator<OverSiteControllerPeopleNumInput, "">;

export const useOverSiteControllerPeopleNum = () => {
	const { viewModel, onSetViewModel } = useProductRoomFormModelContext();
	const {
		overSiteControllerPeopleNum, //
		isExtendSiteControllerPeopleNum,
		capacityPeopleMaximum,
	} = viewModel;

	const onSetIsExtendSiteControllerPeopleNum = useCallback(
		(input: boolean) => {
			onSetViewModel([
				{
					key: "isExtendSiteControllerPeopleNum",
					value: {
						isEdited: true,
						value: input,
					},
				},
				{
					key: "overSiteControllerPeopleNum",
					value: {
						isEdited: true,
						hasError: false,
						value: [],
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const initialValue: OverSiteControllerPeopleNumType = useMemo(() => {
		return isExtendSiteControllerPeopleNum.value
			? // ダイナミックプライシングを有効にしている場合
				"DynamicPrice"
			: (overSiteControllerPeopleNum?.value?.length ?? 0) > 0
				? // ダイナミックプライシングが無効、且つ固定料金の設定が存在する場合
					// （料金が未設定・0円の設定も、超過人数の販売可否に影響するため固定料金として表示する）
					"StaticPrice"
				: // 設定が存在しない場合は未設定
					"NotUse";
	}, [isExtendSiteControllerPeopleNum.value, overSiteControllerPeopleNum?.value?.length]);

	const [overSiteControllerPeopleNumType, setOverSiteControllerPeopleNumType] =
		useState<OverSiteControllerPeopleNumType>(initialValue);

	const onSetOverSiteControllerPeopleNumType = useCallback(
		(input: OverSiteControllerPeopleNumType) => {
			if (input === "DynamicPrice") {
				// "DynamicPrice" が選択された場合
				onSetIsExtendSiteControllerPeopleNum(true);
			} else if (input === "StaticPrice") {
				// "StaticPrice" が選択された場合
				// 休日・平日それぞれに空欄の料金項目を1件自動生成し、料金設定フォームを常時表示する
				const createInitialPrices = () => ({
					isEdited: true,
					hasError: false,
					value: [createEmptyPriceItem(Number(capacityPeopleMaximum.value))],
				});
				onSetViewModel([
					{
						key: "isExtendSiteControllerPeopleNum",
						value: {
							isEdited: true,
							// ダイナミックプライシングを無効にする
							value: false,
						},
					},
					{
						key: "overSiteControllerPeopleNum",
						value: {
							isEdited: true,
							hasError: false,
							value: [
								{ ...WEEKEND_PRICE_DEFAULT_VALUE, prices: createInitialPrices() },
								{ ...WEEKDAY_PRICE_DEFAULT_VALUE, prices: createInitialPrices() },
							],
						},
					},
				]);
			} else {
				// "NotUse" が選択された場合
				onSetIsExtendSiteControllerPeopleNum(false);
			}

			setOverSiteControllerPeopleNumType(input);
		},
		[onSetIsExtendSiteControllerPeopleNum, onSetViewModel, capacityPeopleMaximum.value],
	);

	const overSiteControllerPeopleNumTypeOptions = [
		{
			id: "overSiteControllerPeopleNumType_NotUse",
			label: "設定しない",
			value: "NotUse",
		},
		{
			id: "overSiteControllerPeopleNumType_StaticPrice",
			label: "固定金額で設定する",
			value: "StaticPrice",
		},
		{
			id: "overSiteControllerPeopleNumType_DynamicPrice",
			label: "ダイナミックプライシングで設定する",
			value: "DynamicPrice",
		},
	] satisfies {
		id: string;
		label: string;
		value: OverSiteControllerPeopleNumType;
	}[];

	return {
		overSiteControllerPeopleNumType,
		onSetOverSiteControllerPeopleNumType,
		overSiteControllerPeopleNumTypeOptions,

		overSiteControllerPeopleNum,
		onSetIsExtendSiteControllerPeopleNum,
	};
};
